import React, { useState } from 'react';
import DefaultLayout from 'admin/components/DefaultLayout';
import { useIntl } from 'react-intl';
import { Button, Checkbox, Table, Tabs } from 'ui';
import type { DragEndEvent } from '@dnd-kit/core';
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import useQueryApiClient from 'utils/useQueryApiClient';
import { QUALITY_MARK_ASPECT_RATIO, routes } from 'config/config';
import QualityMarkCreateUpdateDrawer from './QualityMarkCreateUpdateDrawer';
import { QualityMark } from 'interfaces/BackendModels';
import { message } from 'antd';

interface RowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  'data-row-key': string;
}

const Row = (props: RowProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: props['data-row-key'],
  });

  const style: React.CSSProperties = {
    ...props.style,
    transform: CSS.Translate.toString(transform),
    transition,
    cursor: 'move',
    ...(isDragging ? { position: 'relative', zIndex: 9999 } : {}),
  };

  return (
    <tr
      {...props}
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
    />
  );
};

interface QmarkResponse {
  message: string;
  status: number;
}

interface NgUniversalMedal {
  type: string;
  slug: string;
  quality_mark: QualityMark | null;
}

const QMARK_TAB = 'quantity_mark';
const NG_UNIVERSAL_TAB = 'ng_universal';

const QualityMarkListPage = () => {
  const [tableData, setTableData] = useState<any>([]);
  const [isDrawerVisible, setIsDrawerVisible] = useState<boolean>(false);
  const [selectedQmarkId, setSelectedQmarkId] = useState<number>();
  const [selectedNgType, setSelectedNgType] = useState<string>();
  const [activeTab, setActiveTab] = useState<string>(QMARK_TAB);

  const intl = useIntl();

  const { refetch, isLoading: qmarkIsLoading } = useQueryApiClient({
    request: {
      url: 'api/v2/qmark',
    },
    onSuccess: (response) => {
      setTableData(response);
    },
  });

  const {
    data: ngUniversalMedals,
    refetch: refetchNgUniversal,
    isLoading: ngUniversalIsLoading,
  } = useQueryApiClient({
    request: {
      url: 'api/v2/qmark/ng-universal',
    },
  });

  const { appendData, isLoading: qmarkIsSorting } = useQueryApiClient({
    request: {
      url: 'api/v2/qmark/sort',
      method: 'PATCH',
    },
  });

  const { appendData: deleteQmark, isLoading: qmarkIsDeleting } =
    useQueryApiClient({
      request: {
        url: 'api/v2/qmark/:id',
        method: 'DELETE',
      },
      onSuccess: (response: QmarkResponse) => handleDeleteQmark(response),
    });

  const handleDeleteQmark = (response: QmarkResponse) => {
    if (response.status !== 200) {
      return message.warning(
        intl.formatMessage({ id: `message.${response.message}` })
      );
    }

    refetch();
    refetchNgUniversal();

    return message.success(
      intl.formatMessage({ id: `message.${response.message}` })
    );
  };

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (active.id !== over?.id) {
      setTableData((prevState: any) => {
        const activeIndex = prevState.findIndex(
          (record: any) => record.id === active?.id
        );
        const overIndex = prevState.findIndex(
          (record: any) => record.id === over?.id
        );

        const newList = arrayMove(prevState, activeIndex, overIndex);

        appendData({ items: newList });

        return newList;
      });
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 1,
      },
    })
  );

  const openDrawer = (qmarkId?: number, ngType?: string) => {
    setSelectedQmarkId(qmarkId);
    setSelectedNgType(ngType);
    setIsDrawerVisible(true);
  };

  const renderLogo = (value?: string) =>
    value ? (
      <img
        width={80}
        height={80 / QUALITY_MARK_ASPECT_RATIO}
        src={`${routes.api.baseUrl}${value}`}
        alt="logo"
        style={{ objectFit: 'contain' }}
      />
    ) : null;

  const columns = [
    {
      title: intl.formatMessage({ id: 'qmark.id' }),
      dataIndex: 'id',
    },
    {
      title: intl.formatMessage({ id: 'qmark.logo' }),
      dataIndex: 'logo',
      render: renderLogo,
    },
    {
      title: intl.formatMessage({ id: 'qmark.name' }),
      dataIndex: 'name',
    },
    {
      title: intl.formatMessage({ id: 'qmark.description' }),
      dataIndex: 'description',
    },
    {
      title: intl.formatMessage({ id: 'qmark.conflict' }),
      dataIndex: 'conflict',
      render: (value: number[]) =>
        value &&
        value
          .map((item: number) => {
            return tableData.find((e: QualityMark) => e.id === item)?.name;
          })
          .join(', '),
    },
    {
      title: intl.formatMessage({ id: 'qmark.product' }),
      dataIndex: 'product',
      render: (value: boolean) => <Checkbox checked={value} />,
    },
    {
      title: intl.formatMessage({ id: 'general.actions' }),
      dataIndex: 'id',
      render: (value: number) => (
        <>
          <Button
            type="link"
            label={intl.formatMessage({ id: 'general.edit' })}
            onClick={() => openDrawer(value)}
          />
          <Button
            type="link"
            label={intl.formatMessage({ id: 'general.delete' })}
            onClick={() => deleteQmark([], { id: value })}
          />
        </>
      ),
    },
  ];

  const ngUniversalColumns = [
    {
      title: intl.formatMessage({ id: 'qmark.ng_type' }),
      dataIndex: 'type',
      render: (value: string) =>
        intl.formatMessage({ id: `qmark.ng_type.${value}` }),
    },
    {
      title: intl.formatMessage({ id: 'qmark.logo' }),
      dataIndex: ['quality_mark', 'logo'],
      render: renderLogo,
    },
    {
      title: intl.formatMessage({ id: 'qmark.name' }),
      dataIndex: ['quality_mark', 'name'],
    },
    {
      title: intl.formatMessage({ id: 'qmark.description' }),
      dataIndex: ['quality_mark', 'description'],
    },
    {
      title: intl.formatMessage({ id: 'general.actions' }),
      dataIndex: 'type',
      render: (value: string, record: NgUniversalMedal) =>
        record.quality_mark ? (
          <>
            <Button
              type="link"
              label={intl.formatMessage({ id: 'general.edit' })}
              onClick={() => openDrawer(record.quality_mark?.id, value)}
            />
            <Button
              type="link"
              label={intl.formatMessage({ id: 'general.delete' })}
              onClick={() => deleteQmark([], { id: record.quality_mark?.id })}
            />
          </>
        ) : (
          <Button
            type="link"
            label={intl.formatMessage({ id: 'general.add' })}
            onClick={() => openDrawer(undefined, value)}
          />
        ),
    },
  ];

  const items = [
    {
      key: QMARK_TAB,
      label: intl.formatMessage({ id: 'navigation.qmark' }),
      children: (
        <>
          <DndContext
            sensors={sensors}
            modifiers={[restrictToVerticalAxis]}
            onDragEnd={onDragEnd}
          >
            <SortableContext
              items={tableData.map((i: any) => i.id)}
              strategy={verticalListSortingStrategy}
            >
              <Table
                loading={qmarkIsLoading || qmarkIsSorting || qmarkIsDeleting}
                dataSource={tableData}
                columns={columns}
                disablePagination
                components={{ body: { row: Row } }}
              />
            </SortableContext>
          </DndContext>
        </>
      ),
    },
    {
      key: NG_UNIVERSAL_TAB,
      label: intl.formatMessage({ id: 'qmark.ng_universal' }),
      children: (
        <>
          <p>{intl.formatMessage({ id: 'qmark.ng_universal_info' })}</p>
          <Table
            loading={ngUniversalIsLoading || qmarkIsDeleting}
            dataSource={ngUniversalMedals || []}
            columns={ngUniversalColumns}
            rowKey={'type'}
            disablePagination
          />
        </>
      ),
    },
  ];

  const onClose = () => {
    refetch();
    refetchNgUniversal();
    setIsDrawerVisible(false);
    setSelectedQmarkId(undefined);
    setSelectedNgType(undefined);
  };

  return (
    <DefaultLayout.PageLayout
      breadcrumbs={[
        {
          title: intl.formatMessage({ id: 'navigation.beginning' }),
        },
        {
          title: intl.formatMessage({ id: 'navigation.qmark' }),
        },
      ]}
    >
      <DefaultLayout.PageContent>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          tabBarExtraContent={
            activeTab === QMARK_TAB && (
              <Button
                type="primary"
                label={intl.formatMessage({ id: 'general.add' })}
                onClick={() => openDrawer()}
              />
            )
          }
          items={items}
        />

        <QualityMarkCreateUpdateDrawer
          key={JSON.stringify([tableData, ngUniversalMedals])}
          isDrawerVisible={isDrawerVisible}
          id={selectedQmarkId}
          ngType={selectedNgType}
          onClose={onClose}
        />
      </DefaultLayout.PageContent>
    </DefaultLayout.PageLayout>
  );
};

export default QualityMarkListPage;
